import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { PenIcon } from "assets/icons";
import { Button, AlertMessage, CheckBox } from "components/atoms";
import {
  AddressInput,
  TextInput,
  DatePickerInput,
  EmailInput,
  GeneralSelectInput,
  TabbedFormToggle,
  TabConfig,
  EditTabConfig,
  PhoneNumberInput,
  MultiChoiceRadioButtonGroup,
  InformationItem,
} from "components/molecules";
import { useUserData, useContentSanitizer } from "hooks";
import {
  ProfileHandling,
  createPostalLookupService,
  shouldShowAddressState,
  validatePostalCode,
  formatPostalCode,
  formatDate,
  formatDateToString,
  sanitizePhoneNumber,
  formatPhoneNumber,
  formatAddress,
} from "services";
import {
  titleOptions,
  genderOptions,
  maritalStatusOptions,
  countriesFlat,
  countryCodes,
} from "types";

const personalInformationSchema = Yup.object({
  firstName: Yup.string().required("Please enter a first name."),
  lastName: Yup.string().when("isSingleNameOnly", {
    is: false,
    then: (schema) => schema.required("Please enter a last name."),
    otherwise: (schema) => schema.notRequired(),
  }),
  dateOfBirth: Yup.date().required("Please enter your date of birth."),
  gender: Yup.string().required("Please select a gender."),
  customGender: Yup.string()
    .nullable()
    .when("gender", {
      is: "Other",
      then: (schema) => schema.required("Please specify your gender"),
      otherwise: (schema) => schema.nullable(),
    }),
  countryOfBirth: Yup.string().required("Please enter your country of birth."),
  countryOfCitizenship: Yup.string().required(
    "Please enter your country of citizenship."
  ),
  email: Yup.string()
    .email("Please enter a valid email address.")
    .required("Please enter an email address."),
  phone: Yup.object({
    countryCode: Yup.string(),
    areaCode: Yup.string().nullable(),
    number: Yup.string(),
    extension: Yup.string().nullable(),
  }).test(
    "phone-required",
    "Please provide at least one phone number (phone or mobile).",
    function (value) {
      const { mobile } = this.parent;
      const hasPhone = value && value.number && value.number.trim();
      const hasMobile = mobile && mobile.number && mobile.number.trim();

      return hasPhone || hasMobile;
    }
  ),
  mobile: Yup.object({
    countryCode: Yup.string(),
    areaCode: Yup.string().nullable(),
    number: Yup.string(),
    extension: Yup.string().nullable(),
  }).test(
    "mobile-required",
    "Please provide at least one phone number (phone or mobile).",
    function (value) {
      const { phone } = this.parent;
      const hasPhone = phone && phone.number && phone.number.trim();
      const hasMobile = value && value.number && value.number.trim();

      return hasPhone || hasMobile;
    }
  ),
  currentAddress: Yup.object({
    street1: Yup.string().required("Street address is required."),
    street2: Yup.string().nullable(),
    city: Yup.string().required("City is required."),
    state: Yup.string().test(
      "state-required",
      "State/Province is required.",
      function (value) {
        const { country } = this.parent;
        if (!country) return true;

        try {
          const stateRequired = shouldShowAddressState(country);
          return !stateRequired || !!(value && value.trim());
        } catch (error) {
          return !!(value && value.trim());
        }
      }
    ),
    postalCode: Yup.string()
      .required("Postal code is required.")
      .test(
        "postal-code-format",
        "Invalid postal code format for this country.",
        function (value) {
          const { country } = this.parent;
          if (!value || !country) return true;

          try {
            const isValid = validatePostalCode(value, country);
            return !!isValid;
          } catch (error) {
            return !!(value && value.trim());
          }
        }
      ),
    country: Yup.string().required("Country is required."),
  }).required("Please enter your current address."),
  homeAddress: Yup.object({
    street1: Yup.string().required("Street address is required."),
    street2: Yup.string().nullable(),
    city: Yup.string().required("City is required."),
    state: Yup.string().test(
      "state-required",
      "State/Province is required.",
      function (value) {
        const { country } = this.parent;
        if (!country) return true;

        try {
          const stateRequired = shouldShowAddressState(country);
          return !stateRequired || !!(value && value.trim());
        } catch (error) {
          return !!(value && value.trim());
        }
      }
    ),
    postalCode: Yup.string()
      .required("Postal code is required.")
      .test(
        "postal-code-format",
        "Invalid postal code format for this country.",
        function (value) {
          const { country } = this.parent;
          if (!value || !country) return true;

          try {
            const isValid = validatePostalCode(value, country);
            return !!isValid;
          } catch (error) {
            return !!(value && value.trim());
          }
        }
      ),
    country: Yup.string().required("Country is required."),
  }).required("Please enter your permanent address in home country."),
  emergencyContactName: Yup.string().required(
    "Please enter an emergency contact name."
  ),
  emergencyContactRelationship: Yup.string().required(
    "Please specify the relationship."
  ),
  emergencyContactPhone: Yup.object({
    countryCode: Yup.string(),
    areaCode: Yup.string().nullable(),
    number: Yup.string(),
    extension: Yup.string().nullable(),
  }).test(
    "emergency-phone-required",
    "Please provide at least one emergency contact phone number (phone or mobile).",
    function (value) {
      const { phone } = this.parent;
      const hasPhone = phone && phone.number && phone.number.trim();
      const hasMobile = value && value.number && value.number.trim();

      return hasPhone || hasMobile;
    }
  ),
  emergencyContactMobile: Yup.object({
    countryCode: Yup.string(),
    areaCode: Yup.string().nullable(),
    number: Yup.string(),
    extension: Yup.string().nullable(),
  }).test(
    "emergency-mobile-required",
    "Please provide at least one emergency contact phone number (phone or mobile).",
    function (value) {
      const { phone } = this.parent;
      const hasPhone = phone && phone.number && phone.number.trim();
      const hasMobile = value && value.number && value.number.trim();

      return hasPhone || hasMobile;
    }
  ),
  passportNumber: Yup.string().required("Please enter your passport number."),
  passportExpiryDate: Yup.date()
    .nullable()
    .required("Please enter passport expiry date."),
  passportIssuingCountry: Yup.string().required(
    "Please enter passport issuing country."
  ),
  maritalStatus: Yup.string().required("Please choose a marital status."),
  registeredDateOfMarriage: Yup.date()
    .nullable()
    .when("maritalStatus", {
      is: "Married",
      then: (schema) => schema.required("Please choose a date of marriage."),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const PersonalInformationPage: React.FC = () => {
  const { userData, fetchUserData } = useUserData();
  const { sanitizeContent, sanitizeObject } = useContentSanitizer();
  const [editingTab, setEditingTab] = useState<string | null>(null);
  const [lookupTimeouts, setLookupTimeouts] = useState<
    Record<string, NodeJS.Timeout>
  >({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const postalService = createPostalLookupService();

  const formik = useFormik({
    initialValues: {
      title: "",
      firstName: "",
      middleName: "",
      lastName: "",
      isSingleNameOnly: false,
      dateOfBirth: null as Date | null,
      gender: "",
      customGender: "",
      countryOfBirth: "",
      countryOfCitizenship: "",
      isCitizenshipSameAsBirth: false,
      email: "",
      phone: {
        countryCode: "",
        areaCode: "",
        number: "",
        extension: "",
      },
      mobile: {
        countryCode: "",
        areaCode: "",
        number: "",
        extension: "",
      },
      currentAddress: {
        street1: "",
        street2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      },
      homeAddress: {
        street1: "",
        street2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      },
      isHomeSameAsCurrent: false,
      emergencyContactName: "",
      emergencyContactRelationship: "",
      emergencyContactPhone: {
        countryCode: "",
        areaCode: "",
        number: "",
        extension: "",
      },
      emergencyContactMobile: {
        countryCode: "",
        areaCode: "",
        number: "",
        extension: "",
      },
      passportNumber: "",
      passportExpiryDate: null as Date | null,
      passportIssuingCountry: "",
      isPassportSameAsBirth: false,
      maritalStatus: "",
      registeredDateOfMarriage: null as Date | null,
    },
    validationSchema: personalInformationSchema,
    onSubmit: () => {},
  });

  const validatePhoneNumbers = (phone: any, mobile: any) => {
    const hasPhone = phone && phone.number && phone.number.trim();
    const hasMobile = mobile && mobile.number && mobile.number.trim();
    return hasPhone || hasMobile;
  };

  const resetFormToUserData = () => {
    if (!userData) return;

    const {
      basicDetails,
      contactDetails,
      passportDetails,
      maritalStatus,
      registeredDateOfMarriage,
    } = userData.personalInformation;

    const parsePhoneNumber = (phoneNumberData: any) => {
      if (!phoneNumberData) {
        return {
          countryCode: "",
          areaCode: null,
          number: "",
          extension: null,
        };
      }

      return {
        countryCode: phoneNumberData.countryCode || "",
        areaCode: phoneNumberData.areaCode || null,
        number: phoneNumberData.number || "",
        extension: phoneNumberData.extension || null,
      };
    };

    const parseAddress = (addressData: any) => {
      if (!addressData) {
        return {
          street1: "",
          street2: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
        };
      }

      return {
        street1: addressData.street1 || "",
        street2: addressData.street2 || "",
        city: addressData.city || "",
        state: addressData.state || "",
        postalCode: addressData.postalCode || "",
        country: addressData.country || "",
      };
    };

    const storedGender = basicDetails?.gender || "";
    const isCustomGender =
      storedGender &&
      !genderOptions.some((option) => option.value === storedGender);

    formik.setValues({
      title: basicDetails?.title || "",
      firstName: basicDetails?.firstName || "",
      middleName: basicDetails?.middleName || "",
      lastName: basicDetails?.lastName || "",
      isSingleNameOnly: basicDetails?.isSingleNameOnly || false,
      dateOfBirth: basicDetails?.dateOfBirth
        ? new Date(basicDetails.dateOfBirth)
        : null,
      gender: isCustomGender ? "Other" : storedGender,
      customGender: isCustomGender ? storedGender : "",
      countryOfBirth: basicDetails?.countryOfBirth || "",
      countryOfCitizenship: basicDetails?.countryOfCitizenship || "",
      isCitizenshipSameAsBirth: basicDetails?.isCitizenshipSameAsBirth || false,
      email: contactDetails?.email || "",
      phone: parsePhoneNumber(contactDetails?.phone),
      mobile: parsePhoneNumber(contactDetails?.mobile),
      currentAddress: parseAddress(contactDetails?.currentAddress),
      homeAddress: parseAddress(contactDetails?.homeAddress),
      isHomeSameAsCurrent: contactDetails?.isHomeSameAsCurrent || false,
      emergencyContactName: contactDetails?.emergencyContactName || "",
      emergencyContactRelationship:
        contactDetails?.emergencyContactRelationship || "",
      emergencyContactPhone: parsePhoneNumber(
        contactDetails?.emergencyContactPhone
      ),
      emergencyContactMobile: parsePhoneNumber(
        contactDetails?.emergencyContactMobile
      ),
      passportNumber: passportDetails?.passportNumber || "",
      passportExpiryDate: passportDetails?.passportExpiryDate
        ? new Date(passportDetails.passportExpiryDate)
        : null,
      passportIssuingCountry: passportDetails?.passportIssuingCountry || "",
      isPassportSameAsBirth: passportDetails?.isPassportSameAsBirth || false,
      maritalStatus: maritalStatus || "",
      registeredDateOfMarriage: registeredDateOfMarriage
        ? new Date(registeredDateOfMarriage)
        : null,
    });
  };

  const getFormValue = (key: string) =>
    formik.values[key as keyof typeof formik.values];

  const isBasicDetailsComplete = () => {
    const requiredFields = [
      formik.values.firstName,
      formik.values.gender,
      formik.values.dateOfBirth,
      formik.values.countryOfBirth,
      formik.values.countryOfCitizenship,
      formik.values.maritalStatus,
    ];

    if (!formik.values.isSingleNameOnly) {
      requiredFields.push(formik.values.lastName);
    }

    const basicComplete = requiredFields.every(
      (field) => field && field !== ""
    );

    if (formik.values.gender === "Other" && !formik.values.customGender) {
      return false;
    }

    if (
      formik.values.maritalStatus === "Married" &&
      !formik.values.registeredDateOfMarriage
    ) {
      return false;
    }

    return basicComplete;
  };

  const isContactDetailsComplete = () => {
    const currentAddressComplete =
      formik.values.currentAddress &&
      formik.values.currentAddress.street1 &&
      formik.values.currentAddress.city &&
      formik.values.currentAddress.postalCode &&
      formik.values.currentAddress.country;

    const homeAddressComplete =
      formik.values.homeAddress &&
      formik.values.homeAddress.street1 &&
      formik.values.homeAddress.city &&
      formik.values.homeAddress.postalCode &&
      formik.values.homeAddress.country;

    const currentStateValid = formik.values.currentAddress?.country
      ? !shouldShowAddressState(formik.values.currentAddress.country) ||
        (formik.values.currentAddress.state &&
          formik.values.currentAddress.state.trim())
      : true;

    const homeStateValid = formik.values.homeAddress?.country
      ? !shouldShowAddressState(formik.values.homeAddress.country) ||
        (formik.values.homeAddress.state &&
          formik.values.homeAddress.state.trim())
      : true;

    const phoneValid = validatePhoneNumbers(
      formik.values.phone,
      formik.values.mobile
    );

    const hasCurrentAddressErrors = formik.errors.currentAddress;
    const hasHomeAddressErrors = formik.errors.homeAddress;
    const hasPhoneErrors = formik.errors.phone || formik.errors.mobile;

    return !!(
      formik.values.email &&
      phoneValid &&
      currentAddressComplete &&
      currentStateValid &&
      homeAddressComplete &&
      homeStateValid &&
      !hasCurrentAddressErrors &&
      !hasHomeAddressErrors &&
      !hasPhoneErrors
    );
  };

  const isEmergencyDetailsComplete = () => {
    const phoneValid = validatePhoneNumbers(
      formik.values.emergencyContactPhone,
      formik.values.emergencyContactMobile
    );
    const hasPhoneErrors = formik.errors.emergencyContactPhone;

    return !!(
      formik.values.emergencyContactName &&
      formik.values.emergencyContactRelationship &&
      phoneValid &&
      !hasPhoneErrors
    );
  };

  const isPassportDetailsComplete = () => {
    return !!(
      formik.values.passportNumber &&
      formik.values.passportExpiryDate &&
      formik.values.passportIssuingCountry
    );
  };

  const validateFields = async (fields: string[]) => {
    const allErrors = await formik.validateForm();

    const hasRelevantErrors = fields.some((field) => {
      const error = allErrors[field as keyof typeof allErrors];
      if (
        (field === "currentAddress" || field === "homeAddress") &&
        error &&
        typeof error === "object"
      ) {
        return Object.keys(error).length > 0;
      }
      return !!error;
    });

    if (hasRelevantErrors) {
      const touchedFields = fields.reduce((acc, field) => {
        if (field === "currentAddress") {
          return {
            ...acc,
            currentAddress: {
              street1: true,
              street2: true,
              city: true,
              state: true,
              postalCode: true,
              country: true,
            },
          };
        }
        if (field === "homeAddress") {
          return {
            ...acc,
            homeAddress: {
              street1: true,
              street2: true,
              city: true,
              state: true,
              postalCode: true,
              country: true,
            },
          };
        }
        return { ...acc, [field]: true };
      }, {});

      formik.setTouched(touchedFields);
      setErrorMessage("Please fix the validation errors before saving.");
      setSuccessMessage("");
      return false;
    }
    return true;
  };

  const handleSameCountryOfCitizenshipChange = (checked: boolean) => {
    formik.setFieldValue("isCitizenshipSameAsBirth", checked);
    if (checked && formik.values.countryOfBirth) {
      formik.setFieldValue(
        "countryOfCitizenship",
        formik.values.countryOfBirth
      );
    } else if (!checked) {
      formik.setFieldValue("countryOfCitizenship", "");
    }
  };

  const handleSameCurrentAddressChange = async (checked: boolean) => {
    formik.setFieldValue("isHomeSameAsCurrent", checked);
    if (checked && formik.values.currentAddress) {
      const currentAddr = formik.values.currentAddress;

      await formik.setFieldValue("homeAddress", {
        street1: currentAddr.street1 || "",
        street2: currentAddr.street2 || "",
        city: currentAddr.city || "",
        state: currentAddr.state || "",
        postalCode: currentAddr.postalCode || "",
        country: currentAddr.country || "",
      });

      const newTouched = { ...formik.touched };

      if (!newTouched.homeAddress) {
        newTouched.homeAddress = {};
      }

      if (currentAddr.street1) newTouched.homeAddress.street1 = true;
      if (currentAddr.city) newTouched.homeAddress.city = true;
      if (currentAddr.state) newTouched.homeAddress.state = true;
      if (currentAddr.postalCode) newTouched.homeAddress.postalCode = true;
      if (currentAddr.country) newTouched.homeAddress.country = true;

      await formik.setTouched(newTouched);

      setTimeout(() => {
        formik.validateForm();
      }, 0);
    } else if (!checked) {
      await formik.setFieldValue("homeAddress", {
        street1: "",
        street2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      });

      const newTouched = { ...formik.touched };
      delete newTouched.homeAddress;
      await formik.setTouched(newTouched);

      setTimeout(() => {
        formik.validateForm();
      }, 0);
    }
  };

  const handleSamePassportCountryChange = (checked: boolean) => {
    formik.setFieldValue("isPassportSameAsBirth", checked);
    if (checked && formik.values.countryOfBirth) {
      formik.setFieldValue(
        "passportIssuingCountry",
        formik.values.countryOfBirth
      );
    } else if (!checked) {
      formik.setFieldValue("passportIssuingCountry", "");
    }
  };

  const handleCountryOfBirthChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newCountry = e.target.value;
    formik.handleChange(e);

    if (formik.values.isCitizenshipSameAsBirth) {
      formik.setFieldValue("countryOfCitizenship", newCountry);
    }
    if (formik.values.isPassportSameAsBirth) {
      formik.setFieldValue("passportIssuingCountry", newCountry);
    }
  };

  const createAddressHandler = (
    fieldName: "currentAddress" | "homeAddress"
  ) => {
    return async (addressData: any) => {
      const prevValues = formik.values[fieldName];

      if (addressData.postalCode && addressData.country) {
        addressData.postalCode = formatPostalCode(
          addressData.postalCode,
          addressData.country
        );
      }

      await formik.setFieldValue(fieldName, addressData);

      const newTouched = { ...formik.touched };

      if (!newTouched[fieldName]) {
        newTouched[fieldName] = {};
      }

      newTouched[fieldName].street1 = true;
      newTouched[fieldName].city = true;
      newTouched[fieldName].postalCode = true;
      newTouched[fieldName].country = true;

      if (addressData.country && shouldShowAddressState(addressData.country)) {
        newTouched[fieldName].state = true;
      }

      if (fieldName === "currentAddress" && formik.values.isHomeSameAsCurrent) {
        await formik.setFieldValue("homeAddress", addressData);

        if (!newTouched.homeAddress) {
          newTouched.homeAddress = {};
        }

        newTouched.homeAddress.street1 = true;
        newTouched.homeAddress.city = true;
        newTouched.homeAddress.postalCode = true;
        newTouched.homeAddress.country = true;

        if (
          addressData.country &&
          shouldShowAddressState(addressData.country)
        ) {
          newTouched.homeAddress.state = true;
        }
      }

      await formik.setTouched(newTouched);

      setTimeout(() => {
        formik.validateForm();
      }, 0);

      if (
        addressData.postalCode !== prevValues.postalCode &&
        addressData.postalCode &&
        addressData.country &&
        addressData.postalCode.length > 2
      ) {
        if (lookupTimeouts[fieldName]) {
          clearTimeout(lookupTimeouts[fieldName]);
        }

        const timeoutId = setTimeout(async () => {
          try {
            const result = await postalService.lookupByPostalCode(
              addressData.postalCode,
              addressData.country
            );

            if (result.success && result.data) {
              const enhancedAddress = {
                ...addressData,
                street1: addressData.street1 || result.data.street1 || "",
                street2: addressData.street2 || result.data.street2 || "",
                city: addressData.city || result.data.city || "",
                state: addressData.state || result.data.state || "",
                postalCode: addressData.postalCode,
                country: addressData.country,
              };
              await formik.setFieldValue(fieldName, enhancedAddress);

              if (
                fieldName === "currentAddress" &&
                formik.values.isHomeSameAsCurrent
              ) {
                await formik.setFieldValue("homeAddress", enhancedAddress);
              }

              setTimeout(() => {
                formik.validateForm();
              }, 0);
            }
          } catch (error) {
            console.error("Address lookup failed:", error);
          }

          setLookupTimeouts((prev) => {
            const newTimeouts = { ...prev };
            delete newTimeouts[fieldName];
            return newTimeouts;
          });
        }, 800);

        setLookupTimeouts((prev) => ({ ...prev, [fieldName]: timeoutId }));
      }
    };
  };

  const handleCurrentAddressChange = createAddressHandler("currentAddress");
  const handleHomeAddressChange = createAddressHandler("homeAddress");

  const saveBasicDetails = async (): Promise<boolean> => {
    try {
      const fields = [
        "firstName",
        "lastName",
        "gender",
        "customGender",
        "dateOfBirth",
        "countryOfBirth",
        "countryOfCitizenship",
        "maritalStatus",
        "registeredDateOfMarriage",
      ];

      if (!(await validateFields(fields))) return false;

      const finalGender =
        formik.values.gender === "Other"
          ? sanitizeContent(formik.values.customGender)
          : sanitizeContent(formik.values.gender);

      const input = {
        id: (userData as any).id,
        personalInformation: {
          ...userData?.personalInformation,
          basicDetails: {
            ...userData?.personalInformation?.basicDetails,
            title: sanitizeContent(formik.values.title),
            firstName: sanitizeContent(formik.values.firstName),
            middleName: sanitizeContent(formik.values.middleName),
            lastName: sanitizeContent(formik.values.lastName),
            isSingleNameOnly: formik.values.isSingleNameOnly,
            dateOfBirth: formik.values.dateOfBirth
              ? formatDate(getFormValue("dateOfBirth") as Date)
              : null,
            gender: finalGender,
            countryOfBirth: sanitizeContent(formik.values.countryOfBirth),
            countryOfCitizenship: sanitizeContent(
              formik.values.countryOfCitizenship
            ),
            isCitizenshipSameAsBirth: formik.values.isCitizenshipSameAsBirth,
          },
          maritalStatus: sanitizeContent(formik.values.maritalStatus),
          registeredDateOfMarriage:
            formik.values.maritalStatus !== "Married"
              ? null
              : formik.values.registeredDateOfMarriage
                ? formatDate(getFormValue("registeredDateOfMarriage") as Date)
                : null,
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage("Basic details updated successfully.");
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage("Failed to update basic details.");
      setSuccessMessage("");
      return false;
    }
  };

  const saveContactDetails = async (): Promise<boolean> => {
    try {
      const fields = [
        "email",
        "phone",
        "mobile",
        "currentAddress",
        "homeAddress",
      ];

      if (!(await validateFields(fields))) return false;

      const input = {
        id: (userData as any).id,
        personalInformation: {
          ...userData?.personalInformation,
          contactDetails: {
            ...userData?.personalInformation?.contactDetails,
            phone: {
              countryCode: sanitizeContent(formik.values.phone.countryCode),
              areaCode: sanitizeContent(formik.values.phone.areaCode),
              number: sanitizePhoneNumber(
                sanitizeContent(formik.values.phone.number)
              ),
              extension: sanitizeContent(formik.values.phone.extension),
            },
            mobile: {
              countryCode: sanitizeContent(formik.values.mobile.countryCode),
              areaCode: sanitizeContent(formik.values.mobile.areaCode),
              number: sanitizePhoneNumber(
                sanitizeContent(formik.values.mobile.number)
              ),
              extension: sanitizeContent(formik.values.mobile.extension),
            },
            email: sanitizeContent(formik.values.email),
            currentAddress: sanitizeObject(formik.values.currentAddress),
            homeAddress: sanitizeObject(formik.values.homeAddress),
            isHomeSameAsCurrent: formik.values.isHomeSameAsCurrent,
          },
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage("Contact details updated successfully.");
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage("Failed to update contact details.");
      setSuccessMessage("");
      return false;
    }
  };

  const saveEmergencyDetails = async (): Promise<boolean> => {
    try {
      const fields = [
        "emergencyContactName",
        "emergencyContactRelationship",
        "emergencyContactPhone",
        "emergencyContactMobile",
      ];

      if (!(await validateFields(fields))) return false;

      const input = {
        id: (userData as any).id,
        personalInformation: {
          ...userData?.personalInformation,
          contactDetails: {
            ...userData?.personalInformation?.contactDetails,
            emergencyContactName: sanitizeContent(
              formik.values.emergencyContactName
            ),
            emergencyContactRelationship: sanitizeContent(
              formik.values.emergencyContactRelationship
            ),
            emergencyContactPhone: {
              countryCode: sanitizeContent(
                formik.values.emergencyContactPhone.countryCode
              ),
              areaCode: sanitizeContent(
                formik.values.emergencyContactPhone.areaCode
              ),
              number: sanitizePhoneNumber(
                sanitizeContent(formik.values.emergencyContactPhone.number)
              ),
              extension: sanitizeContent(
                formik.values.emergencyContactPhone.extension
              ),
            },
            emergencyContactMobile: {
              countryCode: sanitizeContent(
                formik.values.emergencyContactMobile.countryCode
              ),
              areaCode: sanitizeContent(
                formik.values.emergencyContactMobile.areaCode
              ),
              number: sanitizePhoneNumber(
                sanitizeContent(formik.values.emergencyContactMobile.number)
              ),
              extension: sanitizeContent(
                formik.values.emergencyContactMobile.extension
              ),
            },
          },
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage("Emergency contact updated successfully.");
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage("Failed to update emergency contact.");
      setSuccessMessage("");
      return false;
    }
  };

  const savePassportDetails = async (): Promise<boolean> => {
    try {
      const fields = [
        "passportNumber",
        "passportExpiryDate",
        "passportIssuingCountry",
      ];

      if (!(await validateFields(fields))) return false;

      const input = {
        id: (userData as any).id,
        personalInformation: {
          ...userData?.personalInformation,
          passportDetails: {
            ...userData?.personalInformation?.passportDetails,
            passportNumber: sanitizeContent(formik.values.passportNumber),
            passportExpiryDate: formik.values.passportExpiryDate
              ? formatDate(getFormValue("passportExpiryDate") as Date)
              : null,
            passportIssuingCountry: sanitizeContent(
              formik.values.passportIssuingCountry
            ),
            isPassportSameAsBirth: formik.values.isPassportSameAsBirth,
          },
        },
      };

      await ProfileHandling.updateUser(input);
      await fetchUserData();
      setSuccessMessage("Passport details updated successfully.");
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage("Failed to update passport details.");
      setSuccessMessage("");
      return false;
    }
  };

  useEffect(() => {
    resetFormToUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const HeaderComponent = (
    <div className="form-group header">
      <h2>Personal Information</h2>
    </div>
  );

  const displayTabs: TabConfig[] = [
    {
      id: "basic",
      label: "Basic Details",
      description: "Name, birth, citizenship, marital status",
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>Basic Details</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab("basic")}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="Title:"
              value={String(getFormValue("title") || "Not Provided")}
            />
            <InformationItem
              label="First Name:"
              value={String(getFormValue("firstName") || "Not Provided")}
            />
            {!getFormValue("isSingleNameOnly") && (
              <>
                <InformationItem
                  label="Middle Name:"
                  value={String(getFormValue("middleName") || "Not Provided")}
                />
                <InformationItem
                  label="Last Name:"
                  value={String(getFormValue("lastName") || "Not Provided")}
                />
              </>
            )}
            <InformationItem
              label="Date of Birth:"
              value={
                getFormValue("dateOfBirth")
                  ? formatDateToString(getFormValue("dateOfBirth") as Date)
                  : "Not Provided"
              }
            />
            <InformationItem
              label="Gender:"
              value={String(getFormValue("gender") || "Not Provided")}
            />
            <InformationItem
              label="Country of Birth:"
              value={String(getFormValue("countryOfBirth") || "Not Provided")}
            />
            <InformationItem
              label="Country of Citizenship:"
              value={String(
                getFormValue("countryOfCitizenship") || "Not Provided"
              )}
            />
            <InformationItem
              label="Marital Status:"
              value={String(getFormValue("maritalStatus") || "Not Provided")}
            />
            {getFormValue("maritalStatus") === "Married" && (
              <>
                <InformationItem
                  label="Date of Marriage:"
                  value={
                    getFormValue("registeredDateOfMarriage")
                      ? formatDateToString(
                          getFormValue("registeredDateOfMarriage") as Date
                        )
                      : "Not Provided"
                  }
                />
              </>
            )}
          </dl>
        </div>
      ),
    },
    {
      id: "contact",
      label: "Contact Details",
      description: "Address and phone details",
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>Contact Details</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab("contact")}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="Email:"
              value={String(getFormValue("email") || "Not Provided")}
            />
            <InformationItem
              label="Phone:"
              value={formatPhoneNumber(getFormValue("phone"))}
            />
            <InformationItem
              label="Mobile:"
              value={formatPhoneNumber(getFormValue("mobile"))}
            />
            <InformationItem
              label="Current Address:"
              value={formatAddress(getFormValue("currentAddress"))}
            />
            <InformationItem
              label="Permanent Address in Home Country:"
              value={formatAddress(getFormValue("homeAddress"))}
            />
          </dl>
        </div>
      ),
    },
    {
      id: "emergency",
      label: "Emergency Contact",
      description: "Emergency contact details",
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>Emergency Contact Details</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab("emergency")}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="Emergency Contact Name:"
              value={String(
                getFormValue("emergencyContactName") || "Not Provided"
              )}
            />
            <InformationItem
              label="Relationship:"
              value={String(
                getFormValue("emergencyContactRelationship") || "Not Provided"
              )}
            />
            <InformationItem
              label="Emergency Contact Phone:"
              value={formatPhoneNumber(getFormValue("emergencyContactPhone"))}
            />
            <InformationItem
              label="Emergency Contact Mobile:"
              value={formatPhoneNumber(getFormValue("emergencyContactMobile"))}
            />
          </dl>
        </div>
      ),
    },
    {
      id: "passport",
      label: "Passport Details",
      description: "Travel documents",
      component: (
        <div className="form-section">
          <div className="section-header">
            <h4>Passport Details</h4>
            <Button
              text={<PenIcon />}
              onClick={() => setEditingTab("passport")}
              variant="borderless"
            />
          </div>
          <dl>
            <InformationItem
              label="Passport Number:"
              value={String(getFormValue("passportNumber") || "Not Provided")}
            />
            <InformationItem
              label="Passport Issuing Country:"
              value={String(
                getFormValue("passportIssuingCountry") || "Not Provided"
              )}
            />
            <InformationItem
              label="Passport Expiry Date:"
              value={
                getFormValue("passportExpiryDate")
                  ? formatDateToString(
                      getFormValue("passportExpiryDate") as Date
                    )
                  : "Not Provided"
              }
            />
          </dl>
        </div>
      ),
    },
  ];

  const editTabConfigs: EditTabConfig[] = [
    {
      id: "basic",
      label: "Basic Details",
      title: "Edit Basic Details",
      onSave: saveBasicDetails,
      onCancel: resetFormToUserData,
      saveDisabled: !isBasicDetailsComplete(),
      formFields: (
        <>
          <GeneralSelectInput
            id="title"
            label="Title"
            options={titleOptions}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.title}
            error={formik.errors.title}
            touched={formik.touched.title}
          />
          <CheckBox
            label="I have a single name only"
            checked={formik.values.isSingleNameOnly}
            onChange={(checked) => {
              formik.setFieldValue("isSingleNameOnly", checked);
              if (checked) {
                formik.setFieldValue("lastName", "");
                formik.setFieldValue("middleName", "");
              }
            }}
          />
          <TextInput
            id="firstName"
            label="First Name *"
            placeholder="Enter first name"
            getFieldProps={() => ({
              value: formik.values.firstName,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.firstName}
            touched={formik.touched.firstName}
          />
          {!formik.values.isSingleNameOnly && (
            <TextInput
              id="middleName"
              label="Middle Name"
              placeholder="Enter middle name"
              getFieldProps={() => ({
                value: formik.values.middleName,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              })}
              error={formik.errors.middleName}
              touched={formik.touched.middleName}
            />
          )}
          {!formik.values.isSingleNameOnly && (
            <TextInput
              id="lastName"
              label="Last Name *"
              placeholder="Enter last name"
              getFieldProps={() => ({
                value: formik.values.lastName,
                onChange: formik.handleChange,
                onBlur: formik.handleBlur,
              })}
              error={formik.errors.lastName}
              touched={formik.touched.lastName}
            />
          )}
          <DatePickerInput
            id="dateOfBirth"
            className="dateOfBirth"
            label="Date of Birth"
            value={formik.values.dateOfBirth}
            onChange={(date) => formik.setFieldValue("dateOfBirth", date)}
            preset="pastDate"
          />
          <MultiChoiceRadioButtonGroup
            name="gender"
            label="Gender *"
            options={genderOptions}
            value={formik.values.gender as string}
            otherValue={formik.values.customGender}
            onChange={(value: any) => formik.setFieldValue("gender", value)}
            onOtherChange={(value: any) => {
              formik.setFieldValue("customGender", value);
            }}
            showOther={true}
            otherPlaceholder="Enter gender"
          />
          <GeneralSelectInput
            id="countryOfBirth"
            label="Country of Birth"
            options={countriesFlat}
            onChange={handleCountryOfBirthChange}
            onBlur={formik.handleBlur}
            value={formik.values.countryOfBirth}
            error={formik.errors.countryOfBirth}
            touched={formik.touched.countryOfBirth}
          />
          <GeneralSelectInput
            id="countryOfCitizenship"
            label="Country of Citizenship"
            options={countriesFlat}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.countryOfCitizenship}
            error={formik.errors.countryOfCitizenship}
            touched={formik.touched.countryOfCitizenship}
          />
          <CheckBox
            label="Same as Country of Birth"
            checked={formik.values.isCitizenshipSameAsBirth}
            onChange={handleSameCountryOfCitizenshipChange}
          />
          <GeneralSelectInput
            id="maritalStatus"
            label="Marital Status *"
            options={maritalStatusOptions}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.maritalStatus}
            error={formik.errors.maritalStatus}
            touched={formik.touched.maritalStatus}
          />
          {formik.values.maritalStatus === "Married" && (
            <DatePickerInput
              id="registeredDateOfMarriage"
              className="registeredDateOfMarriage"
              label="Date of Marriage *"
              value={formik.values.registeredDateOfMarriage}
              onChange={(date) =>
                formik.setFieldValue("registeredDateOfMarriage", date)
              }
              preset="pastDate"
            />
          )}
        </>
      ),
    },
    {
      id: "contact",
      label: "Contact Details",
      title: "Edit Contact Details",
      onSave: saveContactDetails,
      onCancel: resetFormToUserData,
      saveDisabled: !isContactDetailsComplete(),
      formFields: (
        <>
          <EmailInput
            id="email"
            label="Email Address *"
            placeholder="Enter email"
            getFieldProps={() => ({
              value: formik.values.email,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.email}
            touched={formik.touched.email}
            disabled={true}
          />
          <PhoneNumberInput
            id="phone"
            label="Phone Number"
            value={formik.values.phone}
            onChange={(phoneData) => {
              formik.setFieldValue("phone", phoneData);
            }}
            onBlur={() => {
              formik.setFieldTouched("phone.countryCode", true);
              formik.setFieldTouched("phone.areaCode", true);
              formik.setFieldTouched("phone.number", true);
              formik.setFieldTouched("phone.extension", true);
            }}
            error={
              typeof formik.errors.phone === "string"
                ? formik.errors.phone
                : undefined
            }
            touched={{
              countryCode: formik.touched.phone?.countryCode,
              areaCode: formik.touched.phone?.areaCode,
              number: formik.touched.phone?.number,
              extension: formik.touched.phone?.extension,
            }}
            countryCodes={countryCodes}
            showExtension={false}
          />
          <PhoneNumberInput
            id="mobile"
            label="Mobile Number"
            value={formik.values.mobile}
            onChange={(phoneData) => {
              formik.setFieldValue("mobile", phoneData);
              formik.setFieldTouched("phone", true);
              formik.setFieldTouched("mobile", true);
            }}
            onBlur={() => {
              formik.setFieldTouched("mobile.countryCode", true);
              formik.setFieldTouched("mobile.areaCode", true);
              formik.setFieldTouched("mobile.number", true);
              formik.setFieldTouched("mobile.extension", true);
            }}
            error={
              typeof formik.errors.mobile === "string"
                ? formik.errors.mobile
                : undefined
            }
            touched={{
              countryCode: formik.touched.mobile?.countryCode,
              areaCode: formik.touched.mobile?.areaCode,
              number: formik.touched.mobile?.number,
              extension: formik.touched.mobile?.extension,
            }}
            countryCodes={countryCodes}
            showExtension={false}
          />

          <AddressInput
            id="currentAddress"
            label="Current Address *"
            value={formik.values.currentAddress}
            onChange={handleCurrentAddressChange}
            onBlur={(fieldName: any) => {
              formik.setFieldTouched(`currentAddress.${fieldName}`, true);
            }}
            countries={countriesFlat}
            errors={{
              street1: formik.errors.currentAddress?.street1,
              street2: formik.errors.currentAddress?.street2,
              city: formik.errors.currentAddress?.city,
              state: formik.errors.currentAddress?.state,
              postalCode: formik.errors.currentAddress?.postalCode,
              country: formik.errors.currentAddress?.country,
            }}
            touched={{
              street1: formik.touched.currentAddress?.street1,
              street2: formik.touched.currentAddress?.street2,
              city: formik.touched.currentAddress?.city,
              state: formik.touched.currentAddress?.state,
              postalCode: formik.touched.currentAddress?.postalCode,
              country: formik.touched.currentAddress?.country,
            }}
          />

          <AddressInput
            id="homeAddress"
            label="Permanent Address in Home Country *"
            value={formik.values.homeAddress}
            onChange={handleHomeAddressChange}
            onBlur={(fieldName: any) => {
              formik.setFieldTouched(`homeAddress.${fieldName}`, true);
            }}
            countries={countriesFlat}
            errors={{
              street1: formik.errors.homeAddress?.street1,
              street2: formik.errors.homeAddress?.street2,
              city: formik.errors.homeAddress?.city,
              state: formik.errors.homeAddress?.state,
              postalCode: formik.errors.homeAddress?.postalCode,
              country: formik.errors.homeAddress?.country,
            }}
            touched={{
              street1: formik.touched.homeAddress?.street1,
              street2: formik.touched.homeAddress?.street2,
              city: formik.touched.homeAddress?.city,
              state: formik.touched.homeAddress?.state,
              postalCode: formik.touched.homeAddress?.postalCode,
              country: formik.touched.homeAddress?.country,
            }}
          />

          <CheckBox
            label="Same as Current Address"
            checked={formik.values.isHomeSameAsCurrent}
            onChange={handleSameCurrentAddressChange}
          />
        </>
      ),
    },
    {
      id: "emergency",
      label: "Emergency Contact",
      title: "Edit Emergency Contact",
      onSave: saveEmergencyDetails,
      onCancel: resetFormToUserData,
      saveDisabled: !isEmergencyDetailsComplete(),
      formFields: (
        <>
          <TextInput
            id="emergencyContactName"
            label="Emergency Contact Name"
            placeholder="Enter emergency contact name"
            getFieldProps={() => ({
              value: formik.values.emergencyContactName,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.emergencyContactName}
            touched={formik.touched.emergencyContactName}
          />
          <TextInput
            id="emergencyContactRelationship"
            label="Relationship"
            placeholder="e.g., Parent, Spouse"
            getFieldProps={() => ({
              value: formik.values.emergencyContactRelationship,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.emergencyContactRelationship}
            touched={formik.touched.emergencyContactRelationship}
          />
          <PhoneNumberInput
            id="emergencyContactPhone"
            label="Emergency Contact Phone"
            value={formik.values.emergencyContactPhone}
            onChange={(phoneData) => {
              formik.setFieldValue("emergencyContactPhone", phoneData);
              formik.setFieldTouched("emergencyContactPhone", true);
              formik.setFieldTouched("emergencyContactMobile", true);
            }}
            onBlur={() => {
              formik.setFieldTouched("emergencyContactPhone.countryCode", true);
              formik.setFieldTouched("emergencyContactPhone.areaCode", true);
              formik.setFieldTouched("emergencyContactPhone.number", true);
              formik.setFieldTouched("emergencyContactPhone.extension", true);
            }}
            error={
              typeof formik.errors.emergencyContactPhone === "string"
                ? formik.errors.emergencyContactPhone
                : undefined
            }
            touched={{
              countryCode: formik.touched.emergencyContactPhone?.countryCode,
              areaCode: formik.touched.emergencyContactPhone?.areaCode,
              number: formik.touched.emergencyContactPhone?.number,
              extension: formik.touched.emergencyContactPhone?.extension,
            }}
            countryCodes={countryCodes}
            showExtension={false}
          />
          <PhoneNumberInput
            id="emergencyContactMobile"
            label="Emergency Contact Mobile"
            value={formik.values.emergencyContactMobile}
            onChange={(phoneData) => {
              formik.setFieldValue("emergencyContactMobile", phoneData);
              formik.setFieldTouched("emergencyContactPhone", true);
              formik.setFieldTouched("emergencyContactMobile", true);
            }}
            onBlur={() => {
              formik.setFieldTouched(
                "emergencyContactMobile.countryCode",
                true
              );
              formik.setFieldTouched("emergencyContactMobile.areaCode", true);
              formik.setFieldTouched("emergencyContactMobile.number", true);
              formik.setFieldTouched("emergencyContactMobile.extension", true);
            }}
            error={
              typeof formik.errors.emergencyContactMobile === "string"
                ? formik.errors.emergencyContactMobile
                : undefined
            }
            touched={{
              countryCode: formik.touched.emergencyContactMobile?.countryCode,
              areaCode: formik.touched.emergencyContactMobile?.areaCode,
              number: formik.touched.emergencyContactMobile?.number,
              extension: formik.touched.emergencyContactMobile?.extension,
            }}
            countryCodes={countryCodes}
            showExtension={false}
          />
        </>
      ),
    },
    {
      id: "passport",
      label: "Passport Details",
      title: "Edit Passport Details",
      onSave: savePassportDetails,
      onCancel: resetFormToUserData,
      saveDisabled: !isPassportDetailsComplete(),
      formFields: (
        <>
          <TextInput
            id="passportNumber"
            label="Passport Number"
            placeholder="Enter passport number"
            getFieldProps={() => ({
              value: formik.values.passportNumber,
              onChange: formik.handleChange,
              onBlur: formik.handleBlur,
            })}
            error={formik.errors.passportNumber}
            touched={formik.touched.passportNumber}
          />
          <GeneralSelectInput
            id="passportIssuingCountry"
            label="Issuing Country"
            options={countriesFlat}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.passportIssuingCountry}
            error={formik.errors.passportIssuingCountry}
            touched={formik.touched.passportIssuingCountry}
          />
          <CheckBox
            label="Same as Country of Birth"
            checked={formik.values.isPassportSameAsBirth}
            onChange={handleSamePassportCountryChange}
          />
          <DatePickerInput
            id="passportExpiryDate"
            className="passportExpiryDate"
            label="Passport Expiry Date"
            value={formik.values.passportExpiryDate}
            onChange={(date) =>
              formik.setFieldValue("passportExpiryDate", date)
            }
            preset="futureDate"
          />
        </>
      ),
    },
  ];

  return (
    <div className="form-group personal-information-page">
      {successMessage && (
        <AlertMessage type="success">{successMessage}</AlertMessage>
      )}
      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}
      <TabbedFormToggle
        headerComponent={HeaderComponent}
        displayTabs={displayTabs}
        editTabConfigs={editTabConfigs}
        formikInstance={formik}
        editingTab={editingTab}
        onTabEditToggle={setEditingTab}
      />
    </div>
  );
};

export default PersonalInformationPage;
